import { useCallback } from 'react';

import { createHook, createStore } from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema';
import {
	encodeAdf,
	encodeSkillPromptToAdf,
} from '@atlassian/conversation-assistant-content-transformer/adf-encoder';

export const PROMPT_PLACEHOLDER_CHAR = '`';

type PlaceholderType = 'person' | 'link' | 'skill' | 'generic';

type PlaceholderData = {
	prompt: string;
	adfDocument?: DocNode;
	type: PlaceholderType;
	selectionStartIndex: number;
	selectionEndIndex: number;
	// Places placeholder type at the cursor position.
	// But should only be used as a last resort, if the placeholder is not inserted at the cursor position by default.
	insertAtCursor?: boolean;
};

type State = {
	placeholder: PlaceholderData | null;
};

const state = createStore({
	initialState: {
		placeholder: null,
	} as State,
	actions: {
		setPlaceholder:
			(placeholder: State['placeholder']) =>
			({ setState }) => {
				setState({ placeholder });
			},
	},
});

// exported for testing
export const usePlaceholderStore = createHook(state);

const getPlaceholderType = (placeholderLabel: string): PlaceholderType => {
	switch (placeholderLabel) {
		case 'person':
			return 'person';
		case 'skill':
			return 'skill';
		default:
			return 'link';
	}
};

const isPromptWithPlaceholder = (prompt: string): boolean => {
	return prompt.includes(PROMPT_PLACEHOLDER_CHAR);
};

export const useEditorPlaceholder = () => {
	const [{ placeholder }, { setPlaceholder }] = usePlaceholderStore();

	const insertPlaceholder = useCallback(
		(prompt: string, overrideType?: PlaceholderType): void => {
			if (overrideType === 'skill') {
				const { adf, promptLength } = encodeSkillPromptToAdf(prompt);

				setPlaceholder({
					prompt,
					adfDocument: adf,
					type: 'generic',
					selectionStartIndex: promptLength,
					selectionEndIndex: promptLength,
				});
				return;
			}

			const startIndexOfPlaceholder = prompt.indexOf(PROMPT_PLACEHOLDER_CHAR);
			const endIndexOfPlaceholder = prompt.lastIndexOf(PROMPT_PLACEHOLDER_CHAR);
			if (startIndexOfPlaceholder !== -1 && endIndexOfPlaceholder !== -1) {
				const placeholderLabel = prompt.slice(startIndexOfPlaceholder + 1, endIndexOfPlaceholder);
				const placeholderType = overrideType ?? getPlaceholderType(placeholderLabel);
				const selectionStartIndex = startIndexOfPlaceholder + 1;
				const selectionEndIndex = endIndexOfPlaceholder;

				const promptWithoutBackticks = prompt.replace(new RegExp(PROMPT_PLACEHOLDER_CHAR, 'g'), '');
				const adfDocument = encodeAdf(promptWithoutBackticks);

				setPlaceholder({
					prompt: promptWithoutBackticks,
					adfDocument,
					type: placeholderType,
					selectionStartIndex,
					selectionEndIndex,
				});
			} else {
				// A proper placeholder was not found
				setPlaceholder({
					prompt: prompt,
					type: 'generic',
					selectionStartIndex: prompt.length + 1,
					selectionEndIndex: prompt.length + 1,
				});
			}
		},
		[setPlaceholder],
	);

	const clearPlaceholder = useCallback((): void => {
		setPlaceholder(null);
	}, [setPlaceholder]);

	return {
		insertPlaceholder,
		clearPlaceholder,
		placeholder,
		isPromptWithPlaceholder,
	};
};
